import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { usePageInfo } from '../../../../config/queries'
import { useVisibleTeamsForUser } from '../../../../hooks/useVisibleTeamsForUser'
import { useUpcomingEventsForUser } from '../../../../hooks/useUpcomingEventsForUser'
import SearchBar from '../../../../components/SearchBar'
import Card from '../../../../components/Card'
import PersonalizedTeams from './PersonalizedTeams'
import PersonalizedEvents from './PersonalizedEvents'
import { imageUrl } from '../../../../helpers'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'

const fallbackImage = '/images/header.jpg'

export const heroSubtitleKey = (
  gameVersion: ReturnType<typeof useGameVersion>,
) => {
  if (gameVersion.has('raid_plans')) return 'homepage.hero-subtitle'
  if (gameVersion.has('guild_candidates'))
    return 'homepage.hero-subtitle-candidates'
  return 'homepage.hero-subtitle-classic'
}

const HeroSection = () => {
  const { t } = useTranslation()
  const reduceMotion = useReducedMotion()
  const {
    data: { user },
  } = usePageInfo()
  const gameVersion = useGameVersion()
  const visibleTeams = useVisibleTeamsForUser(user)
  const upcomingEvents = useUpcomingEventsForUser(user)
  const [imageSrc, setImageSrc] = useState(
    imageUrl(`game-versions/${gameVersion.config.header_image}`),
  )

  return (
    <section className='relative -mt-8 flex w-full flex-col justify-start'>
      <div className='pointer-events-none absolute inset-x-0 top-0 -z-10 h-[40rem] overflow-hidden'>
        <img
          src={imageSrc}
          alt=''
          aria-hidden
          onError={() => setImageSrc(fallbackImage)}
          className='h-full w-full object-cover object-center'
        />
        <div className='absolute inset-0 bg-linear-to-b from-transparent via-background/30 to-background' />
        <div className='absolute inset-0 bg-linear-to-r from-background/60 to-transparent' />
      </div>

      <div className='root-width relative z-10 flex flex-col items-start gap-6 pt-24 pb-12'>
        <motion.div
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className='flex flex-col gap-4'
        >
          <h1 className='max-w-4xl text-5xl leading-tight max-sm:text-3xl'>
            <Trans
              i18nKey='homepage.title'
              components={{ primary: <span className='text-primary' /> }}
            />
          </h1>
          <p className='max-w-2xl text-lg text-muted-foreground max-sm:text-base'>
            {t(heroSubtitleKey(gameVersion))}
          </p>
        </motion.div>

        {visibleTeams.length === 0 ? (
          <motion.div
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <Link
              to='/onboarding'
              className={cn(
                buttonVariants(),
                'gap-2 sm:px-8 sm:py-7 sm:text-xl',
              )}
            >
              {t('homepage.get-started-button')}
              <ArrowRight className='h-5 w-5' />
            </Link>
          </motion.div>
        ) : null}

        <motion.div
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
          className='flex w-full max-w-176 flex-col gap-2'
        >
          <Card className='overflow-hidden p-0'>
            <SearchBar
              placeholder={
                visibleTeams.length > 0
                  ? t('homepage.existing-user-search-bar-text')
                  : t('homepage.new-user-search-bar-text')
              }
              type='inline'
            />
          </Card>

          {visibleTeams.length > 0 ? (
            <Link
              to='/onboarding'
              className='group inline-flex items-center gap-1 self-start text-sm text-muted-foreground transition-colors hover:text-foreground'
            >
              {t('homepage.set-up-new-guild')}
              <ArrowRight className='h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5' />
            </Link>
          ) : null}
        </motion.div>

        {visibleTeams.length > 0 || upcomingEvents.length > 0 ? (
          <motion.div
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
            className='grid w-full grid-cols-1 gap-6 lg:grid-cols-2 mt-16'
          >
            {visibleTeams.length > 0 ? (
              <PersonalizedTeams teams={visibleTeams} />
            ) : null}
            {upcomingEvents.length > 0 ? (
              <PersonalizedEvents events={upcomingEvents} />
            ) : null}
          </motion.div>
        ) : null}
      </div>
    </section>
  )
}

export default HeroSection
